import { Fragment } from 'react';
import { MessageItem } from '../types';
import { ActionButtonsGroup } from './ActionButtonsGroup';
import { RejectTicketDialog } from '../RejectTicketDialog';
import { HoldTicketDialog } from '../HoldTicketDialog';
import { ResumeTicketDialog } from '../ResumeTicketDialog';
import { CloseTicketDialog } from '../CloseTicketDialog';
import { NotificationSnackbar } from '../../../components';
import { useTicketRejection } from './useTicketRejection';
import { useTicketReply } from './useTicketReply';
import { useTicketReopen } from './useTicketReopen';
import { useTicketHold } from './useTicketHold';
import { useTicketResume } from './useTicketResume';
import { useTicketClose } from './useTicketClose';
import { CommentsDialog } from '../../comments/CommentsDialog';

interface MessageActionsProps {
    dataItem: MessageItem;
    approving: boolean;
    onApprove: () => void;
    onReject: () => void;
    onReply: () => void;
    onFlag: () => void;
}

export function MessageActions({ 
    dataItem, 
    approving, 
    onApprove, 
    onReject, 
    onReply, 
    onFlag 
}: MessageActionsProps) {
    const isApproved = dataItem.status === 'Approved';
    const isRejected = dataItem.status === 'Rejected';
    const isOnHold = dataItem.status === 'Hold';
    const isClosed = dataItem.status === 'Closed';
    
    // Rejection functionality
    const { 
        rejectDialogOpen,
        rejecting,
        rejectSuccess,
        rejectError,
        openRejectDialog,
        closeRejectDialog,
        handleRejectSubmit,
        clearSuccessMessage: clearRejectSuccessMessage,
        clearErrorMessage: clearRejectErrorMessage
    } = useTicketRejection({
        ticketId: dataItem.id,
        onSuccess: onReject
    });
    
    // Reply functionality
    const {
        replyDialogOpen,
        openReplyDialog,
        closeReplyDialog
    } = useTicketReply({
        onSuccess: onReply
    });
    
    // Reopen functionality
    const {
        reopening,
        reopenSuccess,
        reopenError,
        handleReopenClick,
        clearSuccessMessage: clearReopenSuccessMessage,
        clearErrorMessage: clearReopenErrorMessage
    } = useTicketReopen({
        ticketId: dataItem.id,
        onSuccess: onReject // Use onReject callback as it refreshes the ticket status
    });
    
    // Hold functionality
    const {
        holdDialogOpen,
        holding,
        holdSuccess,
        holdError,
        openHoldDialog,
        closeHoldDialog,
        handleHoldSubmit,
        clearSuccessMessage: clearHoldSuccessMessage,
        clearErrorMessage: clearHoldErrorMessage
    } = useTicketHold({
        ticketId: dataItem.id,
        onSuccess: onReject // Use onReject callback as it refreshes the ticket status
    });
    
    // Resume functionality
    const {
        resumeDialogOpen,
        resuming,
        resumeSuccess,
        resumeError,
        openResumeDialog,
        closeResumeDialog,
        handleResumeSubmit,
        clearSuccessMessage: clearResumeSuccessMessage,
        clearErrorMessage: clearResumeErrorMessage
    } = useTicketResume({
        ticketId: dataItem.id,
        onSuccess: onReject // Use onReject callback as it refreshes the ticket status
    });
    
    // Close functionality
    const {
        closeDialogOpen,
        closing,
        closeSuccess,
        closeError,
        openCloseDialog,
        closeCloseDialog,
        handleCloseSubmit,
        clearSuccessMessage: clearCloseSuccessMessage,
        clearErrorMessage: clearCloseErrorMessage
    } = useTicketClose({
        ticketId: dataItem.id,
        onSuccess: onReject // Use onReject callback as it refreshes the ticket status
    });
    
    return (
        <Fragment>
            <ActionButtonsGroup 
                isApproved={isApproved}
                isRejected={isRejected}
                isOnHold={isOnHold}
                isClosed={isClosed}
                approving={approving}
                rejecting={rejecting}
                reopening={reopening}
                holding={holding}
                resuming={resuming}
                closing={closing}
                onApproveClick={onApprove}
                onRejectClick={openRejectDialog}
                onReplyClick={openReplyDialog}
                onFlagClick={onFlag}
                onReOpenClick={handleReopenClick}
                onHoldClick={openHoldDialog}
                onResumeClick={openResumeDialog}
                onCloseClick={openCloseDialog}
            />
            
            {/* Reject Dialog */}
            <RejectTicketDialog 
                open={rejectDialogOpen}
                onClose={closeRejectDialog}
                onReject={handleRejectSubmit}
                isRejecting={rejecting}
            />
            
            {/* Comments Dialog */}
            <CommentsDialog 
                open={replyDialogOpen}
                onClose={closeReplyDialog}
                ticketId={dataItem.id}
                comments={dataItem.comments || []}
            />
            
            {/* Hold Dialog */}
            <HoldTicketDialog 
                open={holdDialogOpen}
                onClose={closeHoldDialog}
                onHold={handleHoldSubmit}
                isHolding={holding}
            />
            
            {/* Resume Dialog */}
            <ResumeTicketDialog 
                open={resumeDialogOpen}
                onClose={closeResumeDialog}
                onResume={handleResumeSubmit}
                isResuming={resuming}
            />
            
            {/* Close Dialog */}
            <CloseTicketDialog 
                open={closeDialogOpen}
                onClose={closeCloseDialog}
                onCloseTicket={handleCloseSubmit}
                isClosing={closing}
            />
            
            {/* Rejection Messages */}
            {rejectError && (
                <NotificationSnackbar 
                    open={!!rejectError}
                    message={rejectError}
                    severity="error"
                    onClose={clearRejectErrorMessage}
                />
            )}
            
            {rejectSuccess && (
                <NotificationSnackbar 
                    open={rejectSuccess}
                    message="Ticket rejected successfully"
                    severity="success"
                    onClose={clearRejectSuccessMessage}
                />
            )}

            {/* Reopen Messages */}
            {reopenError && (
                <NotificationSnackbar 
                    open={!!reopenError}
                    message={reopenError}
                    severity="error"
                    onClose={clearReopenErrorMessage}
                />
            )}
            
            {reopenSuccess && (
                <NotificationSnackbar 
                    open={reopenSuccess}
                    message="Ticket reopened successfully"
                    severity="success"
                    onClose={clearReopenSuccessMessage}
                />
            )}
            
            {/* Hold Messages */}
            {holdError && (
                <NotificationSnackbar 
                    open={!!holdError}
                    message={holdError}
                    severity="error"
                    onClose={clearHoldErrorMessage}
                />
            )}
            
            {holdSuccess && (
                <NotificationSnackbar 
                    open={holdSuccess}
                    message="Ticket put on hold successfully"
                    severity="success"
                    onClose={clearHoldSuccessMessage}
                />
            )}

            {/* Resume Messages */}
            {resumeError && (
                <NotificationSnackbar 
                    open={!!resumeError}
                    message={resumeError}
                    severity="error"
                    onClose={clearResumeErrorMessage}
                />
            )}
            
            {resumeSuccess && (
                <NotificationSnackbar 
                    open={resumeSuccess}
                    message="Ticket resumed successfully"
                    severity="success"
                    onClose={clearResumeSuccessMessage}
                />
            )}
            
            {/* Close Messages */}
            {closeError && (
                <NotificationSnackbar 
                    open={!!closeError}
                    message={closeError}
                    severity="error"
                    onClose={clearCloseErrorMessage}
                />
            )}
            
            {closeSuccess && (
                <NotificationSnackbar 
                    open={closeSuccess}
                    message="Ticket closed successfully"
                    severity="success"
                    onClose={clearCloseSuccessMessage}
                />
            )}
        </Fragment>
    );
}